import AuthenticationStore from '~/auth/authenticationStore';
import { getDecodedUserInfoFromToken } from '~/components/LoginPage/utils';

// Office.js type declarations (loaded statically via index.html)
interface OfficeUI {
  messageParent?: (message: string) => void;
}

interface OfficeContext {
  ui?: OfficeUI;
}

interface OfficeGlobal {
  context?: OfficeContext;
  onReady?: (callback: () => void) => void;
}

declare const Office: OfficeGlobal | undefined;

const AS = AuthenticationStore();

/**
 * Get popup mode info from URL params
 * Used by Word add-in to login via popup and receive credentials back via Office Dialog API
 */
export const getPopupModeInfo = (): { isPopupMode: boolean } => {
  const urlParams = new URLSearchParams(window.location.search);
  const isPopupMode = urlParams.get('mode') === 'popup';
  return { isPopupMode };
};

// Initialize Office.js early to prevent timeout errors
// Office.js expects onReady() to be called during initial page load
let officeReadyPromise: Promise<void> | null = null;

const initializeOffice = (): Promise<void> => {
  if (officeReadyPromise) {
    return officeReadyPromise;
  }

  if (typeof Office !== 'undefined' && Office.onReady) {
    officeReadyPromise = new Promise<void>((resolve) => {
      Office.onReady!(() => {
        resolve();
      });
    });
    return officeReadyPromise;
  }

  return Promise.reject(new Error('Office.js not available'));
};

// Initialize Office.js immediately if in popup mode
// This must happen early in the page lifecycle to prevent Office.js timeout errors
if (getPopupModeInfo().isPopupMode) {
  initializeOffice().catch(() => {
    // Office.js not available - will be handled when trying to send credentials
  });
}

/**
 * Send credentials to parent using Office Dialog API
 * Note: The host add-in is responsible for closing the dialog after receiving the message
 */
const sendCredentialsToParent = (): void => {
  const accessToken = AS.getAccessToken();
  if (!accessToken) {
    console.warn('Popup mode: No access token found');
    return;
  }

  const userInfo = getDecodedUserInfoFromToken(accessToken);

  const message = JSON.stringify({
    type: 'EVISORT_LOGIN_SUCCESS',
    token: accessToken,
    evisortUserId: userInfo.user_id,
    clientId: userInfo.client_id,
  });

  // Use Office Dialog API to send message back to add-in
  // The host add-in will close the dialog after receiving this message
  if (typeof Office !== 'undefined' && Office.context?.ui?.messageParent) {
    Office.context.ui.messageParent(message);
  } else {
    console.error('Office.context.ui.messageParent not available');
  }
};

/**
 * Handle successful login when in popup mode
 * Uses Office Dialog API to send credentials back to the add-in
 * Office.js must be loaded statically in index.html for this to work
 */
export const handlePopupLoginSuccess = (): void => {
  if (officeReadyPromise) {
    // Office.js was initialized early, wait for it to be ready then send credentials
    officeReadyPromise
      .then(() => {
        sendCredentialsToParent();
      })
      .catch(() => {
        console.error('Office.js initialization failed');
      });
  } else {
    // Fallback: try to initialize now (may fail if Office.js has timeout)
    initializeOffice()
      .then(() => {
        sendCredentialsToParent();
      })
      .catch(() => {
        console.error(
          'Office.js not loaded - check that office.js is in index.html',
        );
      });
  }
};

/**
 * Wait for Office.js to be ready, then handle popup login success
 * Use this when checking for already-authenticated users on page load
 */
export const handlePopupLoginSuccessWhenReady = (): void => {
  if (officeReadyPromise) {
    officeReadyPromise
      .then(() => {
        sendCredentialsToParent();
      })
      .catch(() => {
        // Office.js not available, silently fail
      });
  } else {
    // Fallback: try to initialize now (Office.js may have loaded late)
    initializeOffice()
      .then(() => {
        sendCredentialsToParent();
      })
      .catch(() => {
        // Office.js not available - silently fail for already-authenticated users
        // (they can still use the app normally, just not via the add-in popup)
      });
  }
};

// Export for testing
export const _test = {
  sendCredentialsToParent,
  resetLoadPromise: () => {
    officeReadyPromise = null;
  },
};
